import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import { useEntities } from '@/api/entities';
import { useEntityStore } from '@/store/entity';
import { radius } from '@/theme/tokens';

/**
 * Topbar entity selector (wireframe pill): "ENTITY  [CON001] Convergence IT (IN)".
 * Opens a searchable list with code badges and a tick on the active entity.
 * The selection is persisted (useEntityStore) so it survives navigation and
 * reloads; pages read the active entity_id from the store.
 *
 * Auto-pick rule: when the API returns exactly ONE entity (the usual case for
 * ESS users), it becomes the active entity and the pill is read-only. Staff
 * with several entities start on "All entities".
 */
export function EntitySelector() {
  const { data: entities, isLoading } = useEntities();
  const entityId = useEntityStore((s) => s.entityId);
  const setEntity = useEntityStore((s) => s.setEntity);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [q, setQ] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);

  const active = useMemo(() => entities?.filter((e) => !e.is_deleted) ?? [], [entities]);
  const current = active.find((e) => e.id === entityId) ?? null;
  const single = !isLoading && active.length === 1;

  // Drop a persisted entity that no longer exists, so the pill is never stuck on a phantom.
  useEffect(() => {
    if (!isLoading && entityId && active.length > 0 && !active.some((e) => e.id === entityId)) setEntity(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, active.length, entityId]);

  // Exactly one entity and nothing chosen: choose it.
  useEffect(() => {
    if (!isLoading && active.length === 1 && !entityId) setEntity(active[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, active.length, entityId]);

  const needle = q.trim().toLowerCase();
  const shown = active.filter((e) => !needle
    || `${e.short_code} ${e.name} ${e.country_code} ${e.state_province ?? ''}`.toLowerCase().includes(needle));
  const pick = (id: string | null) => { setEntity(id); setAnchor(null); setQ(''); };

  return (
    <>
      <ButtonBase
        disabled={isLoading || single}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="listbox"
        aria-label="Entity"
        sx={{
          height: 38, px: 2, gap: 1, borderRadius: '19px', border: '1px solid #e1e5e8', bgcolor: '#fff',
          fontFamily: 'inherit', maxWidth: { xs: 220, sm: 420 }, minWidth: 0,
          '&:hover': { borderColor: '#c9d3dc' },
          ...(anchor && { borderColor: '#c9d3dc', bgcolor: '#f8fafb' }),
          '&.Mui-disabled': { opacity: 1 },
        }}>
        <Box component="span" sx={{ fontSize: 11.5, letterSpacing: '.6px', color: '#8a95a1', display: { xs: 'none', sm: 'inline' } }}>
          ENTITY
        </Box>
        {current ? (
          <>
            <CodeBadge>{current.short_code}</CodeBadge>
            <Box component="span" sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {current.name}
            </Box>
            <Box component="span" sx={{ fontSize: 12.5, color: '#8a95a1' }}>({current.country_code})</Box>
          </>
        ) : (
          <Box component="span" sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>
            {isLoading ? 'Loading…' : 'All entities'}
          </Box>
        )}
        {!single && <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#8a95a1', ml: 0.25 }} />}
      </ButtonBase>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => { setAnchor(null); setQ(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        TransitionProps={{ onEntered: () => searchRef.current?.focus() }}
        slotProps={{ paper: { sx: { mt: 1, width: 400, maxWidth: 'calc(100vw - 32px)', borderRadius: radius.dialog,
          boxShadow: '0 12px 32px rgba(0,0,0,.12)' } } }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid #eef2f4' }}>
          <TextField size="small" fullWidth placeholder="Search entity…" value={q} inputRef={searchRef}
            sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#c9d3dc', borderWidth: 1 } }}
            onChange={(e) => setQ(e.target.value)}
            // preventDefault: otherwise the Enter re-opens the popup once focus returns to the pill.
            onKeyDown={(e) => { if (e.key === 'Enter' && shown.length === 1) { e.preventDefault(); pick(shown[0].id); } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#8a95a1' }} /></InputAdornment> }} />
        </Box>
        <Box role="listbox" sx={{ p: 1, maxHeight: 360, overflowY: 'auto' }}>
          {!needle && (
            <Row selected={!entityId} badge="ALL" title="All entities" onClick={() => pick(null)} />
          )}
          {shown.map((e) => (
            <Row key={e.id} selected={e.id === entityId} badge={e.short_code} title={e.name}
              sub={[e.country_code, e.state_province].filter(Boolean).join(' · ')}
              onClick={() => pick(e.id)} />
          ))}
          {shown.length === 0 && (
            <Box sx={{ px: 1.5, py: 2, fontSize: 13, color: 'text.secondary' }}>No entity matches “{q}”.</Box>
          )}
        </Box>
      </Popover>
    </>
  );
}

function CodeBadge({ children, wide }: { children: string; wide?: boolean }) {
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 22, px: 1,
      minWidth: wide ? 72 : 0, borderRadius: '5px', bgcolor: '#e8f4fd', color: '#108ae6',
      fontSize: 12, fontWeight: 700, letterSpacing: '.3px', whiteSpace: 'nowrap', flexShrink: 0 }}>
      {children}
    </Box>
  );
}

function Row({ selected, badge, title, sub, onClick }: {
  selected: boolean; badge: string; title: string; sub?: string; onClick: () => void;
}) {
  return (
    <ButtonBase role="option" aria-selected={selected} onClick={onClick} sx={{
      width: '100%', justifyContent: 'flex-start', gap: 1.5, px: 1.5, py: 1, borderRadius: '8px',
      fontFamily: 'inherit', textAlign: 'left',
      bgcolor: selected ? '#eef6fd' : 'transparent', '&:hover': { bgcolor: selected ? '#e5f1fc' : '#f6f8fa' } }}>
      <CodeBadge wide>{badge}</CodeBadge>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </Box>
        {sub && <Box sx={{ fontSize: 12, color: '#8a95a1', mt: '1px' }}>{sub}</Box>}
      </Box>
      {selected && <CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
    </ButtonBase>
  );
}
